import { useEffect, useState } from 'react';
import { Image, ImageSourcePropType, Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import BookCard from '../components/BookCard';
import BottomTabBar from '../components/BottomTabBar';
import SectionHeader from '../components/SectionHeader';
import { ActionHeader } from '../components/AppHeader';
import colors from '../theme';
import { RootStackParamList } from '../navigation/RootNavigator';
import { Audiobook, PersonDetailData, getPersonDetail } from '../api/audiobooks';
import { htmlToPlainText } from '../utils/html';
import { useFavoritePersonState } from '../hooks/useFavoritePersonState';
import { narratorName } from '../utils/audiobookPeople';

type Props = NativeStackScreenProps<RootStackParamList, 'AuthorDetails'>;

const authorImage = require('../../assets/aural-author-3.png');
const bookFallback = require('../../assets/aural-book-1.png');

function imageSource(url: string | null | undefined, fallback: ImageSourcePropType): ImageSourcePropType {
  return url ? { uri: url } : fallback;
}

function bookAuthor(book: Audiobook) {
  return narratorName(book);
}

function personRole(role: unknown) {
  if (role && typeof role === 'object') {
    const maybeRole = role as { label?: unknown; value?: unknown };
    if (typeof maybeRole.label === 'string') return maybeRole.label;
    if (typeof maybeRole.value === 'string') return personRole(maybeRole.value);
    return '';
  }

  if (role === 'author') return 'Tác giả';
  if (role === 'narrator') return 'Người đọc sách';
  if (role === 'both') return 'Người đọc sách';
  return typeof role === 'string' ? role : '';
}

function AuthorSkeleton() {
  return (
    <>
      <View style={styles.profileRow}>
        <View style={[styles.avatar, styles.skeletonBlock]} />
        <View style={styles.profileInfo}>
          <View style={[styles.skeletonBlock, styles.skeletonName]} />
          <View style={[styles.skeletonBlock, styles.skeletonRole]} />
        </View>
      </View>
      <View style={[styles.skeletonBlock, styles.skeletonBio]} />
      <View style={styles.section}>
        <View style={[styles.skeletonBlock, styles.skeletonSectionTitle]} />
        <ScrollView horizontal showsHorizontalScrollIndicator={false}>
          {[0, 1, 2].map((item) => (
            <View key={item} style={styles.skeletonCard}>
              <View style={[styles.skeletonBlock, styles.skeletonCover]} />
              <View style={[styles.skeletonBlock, styles.skeletonBookTitle]} />
              <View style={[styles.skeletonBlock, styles.skeletonBookAuthor]} />
            </View>
          ))}
        </ScrollView>
      </View>
    </>
  );
}

export default function AuthorDetailsScreen({ navigation, route }: Props) {
  const [detail, setDetail] = useState<PersonDetailData | null>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let mounted = true;
    setLoaded(false);
    setDetail(null);

    if (!route.params?.id) {
      setLoaded(true);
      return () => {
        mounted = false;
      };
    }

    getPersonDetail(route.params.id)
      .then((data) => {
        if (mounted) setDetail(data);
      })
      .catch(() => {
        if (mounted) setDetail(null);
      })
      .finally(() => {
        if (mounted) setLoaded(true);
      });

    return () => {
      mounted = false;
    };
  }, [route.params?.id]);

  const person = detail?.person;
  const favoriteState = useFavoritePersonState(person?.id ?? route.params?.id, () => navigation.navigate('Login'));
  const aboutText = htmlToPlainText(person?.bio ?? person?.description);
  const books = detail?.audiobooks?.map((book) => ({
    key: String(book.id),
    id: book.id,
    slug: book.slug,
    title: book.name,
    author: bookAuthor(book),
    image: imageSource(book.cover, bookFallback),
  })) ?? [];

  return (
    <View style={styles.screen}>
      <ScrollView contentContainerStyle={styles.container} showsVerticalScrollIndicator={false}>
        <ActionHeader
          onBack={() => navigation.goBack()}
          rightIcons={[favoriteState.favorite ? 'heart' : 'heart-outline', 'ellipsis-horizontal']}
          onRightPress={[favoriteState.toggle]}
        />

        {!loaded ? (
          <AuthorSkeleton />
        ) : person ? (
          <>
            <View style={styles.profileRow}>
              <Image source={imageSource(person.avatar, authorImage)} style={styles.avatar} resizeMode="cover" />
              <View style={styles.profileInfo}>
                <Text style={styles.name}>{person.name}</Text>
                <Text style={styles.role}>{personRole(person.role)}</Text>
              </View>
            </View>

            <View style={styles.aboutBox}>
              <SectionHeader title="Giới thiệu" />
              <Text style={styles.aboutText}>{aboutText || 'Thông tin người đọc sách đang được cập nhật.'}</Text>
            </View>

            <View style={styles.section}>
              <SectionHeader
                title="Sách nói"
                actionText="Thêm"
                onAction={() => navigation.navigate('Downloads', { title: person.name, person_id: person.id })}
              />
              {books.length ? (
                <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.sliderContent}>
                  {books.map((book) => (
                    <BookCard
                      key={book.key}
                      title={book.title}
                      author={book.author}
                      imageSource={book.image}
                      onPress={() => navigation.navigate('BookOverview', { id: book.id, slug: book.slug })}
                    />
                  ))}
                </ScrollView>
              ) : (
                <Text style={styles.emptyText}>Chưa có sách nói cho người đọc sách này.</Text>
              )}
            </View>
          </>
        ) : (
          <View style={styles.emptyState}>
            <Text style={styles.emptyTitle}>Không tìm thấy thông tin</Text>
            <Text style={styles.emptyText}>Vui lòng quay lại và thử chọn người đọc sách khác.</Text>
          </View>
        )}
      </ScrollView>
      <BottomTabBar
        active="My Library"
        onHome={() => navigation.navigate('Home')}
        onDiscover={() => navigation.navigate('GetSuggestions')}
        onLibrary={() => navigation.navigate('Library')}
        onProfile={() => navigation.navigate('Settings')}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: 'rgb(242, 249, 253)',
  },
  container: {
    paddingHorizontal: 24,
    paddingTop: 62,
    paddingBottom: 124,
  },
  profileRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 24,
  },
  avatar: {
    width: 88,
    height: 88,
    borderRadius: 44,
    backgroundColor: colors.card,
    marginRight: 20,
  },
  profileInfo: {
    flex: 1,
  },
  name: {
    color: colors.textPrimary,
    fontSize: 18,
    fontWeight: '700',
    marginBottom: 8,
  },
  role: {
    color: colors.textSecondary,
    fontSize: 14,
  },
  aboutBox: {
    backgroundColor: colors.panel,
    borderRadius: 8,
    padding: 0,
    marginBottom: 24,
  },
  aboutText: {
    color: colors.textSecondary,
    fontSize: 14,
    lineHeight: 21,
    marginBottom: 12,
  },
  readMore: {
    color: colors.button,
    fontWeight: '700',
    fontSize: 14,
  },
  section: {
    marginBottom: 24,
  },
  sliderContent: {
    paddingRight: 24,
  },
  emptyState: {
    paddingVertical: 32,
  },
  emptyTitle: {
    color: colors.textPrimary,
    fontSize: 18,
    fontWeight: '700',
    marginBottom: 8,
  },
  emptyText: {
    color: colors.textSecondary,
    fontSize: 14,
    lineHeight: 21,
  },
  skeletonBlock: { backgroundColor: '#ECEEF2' },
  skeletonName: { width: 150, height: 20, borderRadius: 6, marginBottom: 10 },
  skeletonRole: { width: 72, height: 14, borderRadius: 6 },
  skeletonBio: { height: 108, borderRadius: 8, marginBottom: 24 },
  skeletonSectionTitle: { width: 120, height: 20, borderRadius: 6, marginBottom: 16 },
  skeletonCard: { width: 140, marginRight: 16 },
  skeletonCover: { width: 140, height: 140, borderRadius: 8, marginBottom: 16 },
  skeletonBookTitle: { width: 118, height: 16, borderRadius: 6, marginBottom: 8 },
  skeletonBookAuthor: { width: 82, height: 12, borderRadius: 6 },
});
